import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { defaultLocation, calculationMethods } from '../data/prayerData';

const PrayerContext = createContext(null);

export const usePrayer = () => {
  const context = useContext(PrayerContext);
  if (!context) {
    throw new Error('usePrayer must be used within a PrayerProvider');
  }
  return context;
};

export const PrayerProvider = ({ children }) => {
  const [prayerTimes, setPrayerTimes] = useState(null);
  const [extraTimings, setExtraTimings] = useState(null);
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [calculationMethod, setCalculationMethod] = useState(() => {
    const saved = localStorage.getItem('calculationMethod');
    return saved !== null ? parseInt(saved, 10) : 4; // Default: Umm Al-Qura
  });
  const [hijriDate, setHijriDate] = useState(null);

  const fetchPrayerTimes = useCallback(async (lat, lng, method = calculationMethod) => {
    try {
      setLoading(true);
      const today = new Date();
      const dateStr = `${today.getDate()}-${today.getMonth() + 1}-${today.getFullYear()}`;
      
      const response = await fetch(
        `https://api.aladhan.com/v1/timings/${dateStr}?latitude=${lat}&longitude=${lng}&method=${method}`
      );
      
      if (!response.ok) throw new Error('Failed to fetch prayer times');
      
      const data = await response.json();
      const timings = data.data.timings;
      setPrayerTimes(timings);
      // Store full timings object (includes Imsak, Sunrise, Midnight etc.)
      setExtraTimings({
        Imsak:      timings.Imsak,
        Sunrise:    timings.Sunrise,
        Sunset:     timings.Sunset,
        Midnight:   timings.Midnight,
        Firstthird: timings.Firstthird,
        Lastthird:  timings.Lastthird,
      });
      setHijriDate(data.data.date.hijri);
      setError(null);
    } catch (err) {
      console.error('Prayer times fetch error:', err);
      setError('Failed to load prayer times');
    } finally {
      setLoading(false);
    }
  }, [calculationMethod]);

  const getUserLocation = useCallback(() => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          
          // Reverse geocode to get city name
          try {
            const geoResponse = await fetch(
              `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
            );
            const geoData = await geoResponse.json();
            
            setLocation({
              latitude,
              longitude,
              city: geoData.city || geoData.locality || 'Unknown',
              country: geoData.countryName || 'Unknown'
            });
            
            fetchPrayerTimes(latitude, longitude);
          } catch {
            setLocation({ latitude, longitude, city: 'Current Location', country: '' });
            fetchPrayerTimes(latitude, longitude);
          }
        },
        (err) => {
          console.log('Geolocation denied, using default:', err);
          setLocation(defaultLocation);
          fetchPrayerTimes(defaultLocation.latitude, defaultLocation.longitude);
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    } else {
      setLocation(defaultLocation);
      fetchPrayerTimes(defaultLocation.latitude, defaultLocation.longitude);
    }
  }, [fetchPrayerTimes]);

  useEffect(() => {
    getUserLocation();
  }, [getUserLocation]);

  const updateCalculationMethod = (methodId) => {
    setCalculationMethod(methodId);
    localStorage.setItem('calculationMethod', methodId.toString());
    if (location) {
      fetchPrayerTimes(location.latitude, location.longitude, methodId);
    }
  };

  const refreshLocation = () => {
    getUserLocation();
  };

  return (
    <PrayerContext.Provider
      value={{
        prayerTimes,
        extraTimings,
        location,
        loading,
        error,
        hijriDate,
        calculationMethod,
        calculationMethods,
        updateCalculationMethod,
        refreshLocation,
        fetchPrayerTimes,
      }}
    >
      {children}
    </PrayerContext.Provider>
  );
};
